import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, Plus, Search } from "lucide-react";
import { authAPI } from "../../services/api";
import cacheService from "../../services/cacheService";
import Calendar from "./dashboardComponents/calender";

export default function FarmManagement() {
  const location = useLocation();
  const [expandedItem, setExpandedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [farmItems, setFarmItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const farmTypes = ["crop", "animal"];

  useEffect(() => {
    const fetchFarmData = async (forceRefresh = false) => {
      try {
        // Only show loading if we don't have items to avoid flickering
        if (farmItems.length === 0) {
          setLoading(true);
        }

        // Try to get from cache first for immediate display
        // Use method 'GET' and correct URL
        const cachedResult = await cacheService.getCachedResponse('/manage/farm', 'GET');
        if (cachedResult && cachedResult.status && cachedResult.data && farmItems.length === 0) {
          const formattedItems = cachedResult.data.map(item => ({
            id: item.id,
            name: item.name,
            type: "crop",
            image: item.image,
            startDate: item.planting_date || "Not planted yet",
            expectedHarvest: item.harvest_date || "Not harvested yet",
            expenses: item.total_expenses || 0,
            careExpenses: item.care_cost || 0,
            stages: item.growing_stages?.map(stage => ({
              name: stage.stage_name,
              date: new Date(stage.start_date).toISOString().split('T')[0],
              endDate: new Date(stage.end_date).toISOString().split('T')[0],
              cost: stage.stage_cost || 0,
              current: stage.is_current || false
            })) || [],
            currentStage: item.growing_stages?.find(stage => stage.is_current)?.stage_name || "Not started"
          }));

          setFarmItems(formattedItems);
          setLoading(false); // Hide loading once we have cache
        }

        const result = await authAPI.getFarmManagement();

        if (result.status) {
          const formattedItems = result.data.map(item => ({
            id: item.id,
            name: item.name,
            type: "crop",
            image: item.image,
            startDate: item.planting_date || "Not planted yet",
            expectedHarvest: item.harvest_date || "Not harvested yet",
            expenses: item.total_expenses || 0,
            careExpenses: item.care_cost || 0,
            stages: item.growing_stages?.map(stage => ({
              name: stage.stage_name,
              date: new Date(stage.start_date).toISOString().split('T')[0],
              endDate: new Date(stage.end_date).toISOString().split('T')[0],
              cost: stage.stage_cost || 0,
              current: stage.is_current || false
            })) || [],
            currentStage: item.growing_stages?.find(stage => stage.is_current)?.stage_name || "Not started"
          }));

          setFarmItems(formattedItems);
        }
      } catch (error) {
        console.error('Error fetching farm data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFarmData();

    // Add event listener for page visibility to refresh when returning from other pages
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchFarmData(true); // Force refresh
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also listen for browser navigation (back/forward)
    const handleFocus = () => {
      fetchFarmData(true); // Force refresh
    };

    window.addEventListener('focus', handleFocus);

    const handleSyncComplete = () => {
      fetchFarmData(true);
    };

    window.addEventListener('sync-complete', handleSyncComplete);
    window.addEventListener('online', handleSyncComplete);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('sync-complete', handleSyncComplete);
      window.removeEventListener('online', handleSyncComplete);
    };
  }, [location.pathname]); // Re-fetch when location changes

  const toggleItem = (id) => {
    setExpandedItem(expandedItem === id ? null : id);
  };

  const getStageColor = (stage) => {
    const colors = {
      Planting: "#3498db",
      Germination: "#9b59b6",
      Vegetative: "#2ecc71",
      Flowering: "#e74c3c",
      Fruiting: "#f39c12",
      Harvest: "#0e4410",
      Newborn: "#3498db",
      Juvenile: "#9b59b6",
      Growth: "#2ecc71",
      Mature: "#f39c12",
      Breeding: "#e74c3c"
    };
    return colors[stage] || "#95a5a6";
  };

  const filteredItems = farmItems.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "All" || item.type === typeFilter;

    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="farm-management-container">
        <div className="farm-header">
          <h1>My Farm</h1>
        </div>
        <div className="loading-message">Loading farm data...</div>
      </div>
    );
  }

  return (
    <div className="farm-management-container">
      <div className="farm-header">
        <h1>My Farm</h1>
      </div>


      <div className="sell-search-bar_ffx">
        <div className="search-wrapper_ffx">
          <div className="search-icon_ffx">
            <Search size={18} />
          </div>
          <input
            type="text"
            className="search-input_ffx"
            placeholder="Search farm items by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filters_ffx">
          <div className="filter-select-wrapper_ffx">
            <label htmlFor="type-filter" className="filter-label_ffx">
              Type:
            </label>
            <select
              id="type-filter"
              className="filter-select_ffx"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="All">All</option>
              {farmTypes.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>


        </div>
      </div>

      <div className="farm-produce-list">
        {filteredItems.length > 0 ? (
          filteredItems.map((item) => (
            <div
              key={item.id}
              className={`produce-item ${expandedItem === item.id ? 'expanded' : ''}`}
            >
              <div
                className="produce-header"
                onClick={() => toggleItem(item.id)}
              >
                <div className="produce-info">
                  <img src={item.image} alt={item.name} className="produce-image" />
                  <div className="produce-details">
                    <h3>{item.name}</h3>
                    <span
                      className="stage-badge"
                      style={{ backgroundColor: getStageColor(item.currentStage) }}
                    >
                      {item.currentStage}
                    </span>
                  </div>
                </div>

                {expandedItem === item.id ? <ChevronUp /> : <ChevronDown />}
              </div>

              {expandedItem === item.id && (
                <div className="produce-content">
                  <div className="content-section">
                    <h4>Growth Timeline</h4>
                    <div className="stage-timeline">
                      {item.stages.map((stage, index) => (
                        <div
                          key={index}
                          className={`stage ${stage.current ? 'current' : ''} ${stage.date > new Date().toISOString().split('T')[0] ? 'future' : ''}`}
                        >
                          <div
                            className="stage-marker"
                            style={{ backgroundColor: getStageColor(stage.name) }}
                          ></div>
                          <div className="stage-info">
                            <span className="stage-name">{stage.name}</span>
                            <span className="stage-date">{stage.date}</span>
                            {stage.cost > 0 && (
                              <span className="stage-cost">₦{stage.cost.toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="content-grid">
                    <div className="content-cell">
                      <h4>Start Date</h4>
                      <p>{item.startDate}</p>
                    </div>
                    <div className="content-cell">
                      <h4>Expected {item.type === 'crop' ? 'Harvest' : 'Maturity'}</h4>
                      <p>{item.expectedHarvest}</p>
                    </div>
                    <div className="content-cell">
                      <h4>Total Expenses</h4>
                      <p>₦{item.expenses.toLocaleString()}</p>
                    </div>
                    <div className="content-cell">
                      <h4>Care Expenses</h4>
                      <p>₦{item.careExpenses.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="action-buttons">
                    <Link
                      to={`/farmer-dashboard/myfarm/manage/${item.id}`}
                      className="manage-btn"
                    >
                      Manage Produce
                    </Link>

                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="no-products_ffx">
            <p>No farm items found matching your search criteria.</p>
          </div>
        )}
      </div>
      <div className="calender-section">
        <div className="farm-header" style={{ marginBottom: "20px" }}>
          <h1 >My Calender</h1>
        </div>

        <Calendar />
      </div>
    </div>
  );
}